import queryString from 'query-string'
import { subDays, subMonths, format, formatDistanceStrict } from 'date-fns'
import { log } from 'src/services/app/log.service'
import { notifyMsgs, notifyService } from 'src/services/app/notify.service'
import {
  DEFAULT_TABLE_ROWS_PER_PAGE,
  TABLE_ROWS_PER_PAGE_OPTIONS,
} from 'src/services/consts/app.const'
import { validateUrlProtocol } from 'src/services/helpers/security.helper'

export function isObjectEmpty(obj) {
  if (!obj || typeof obj !== 'object') return false
  return Object.keys(obj).length === 0
}

/**
 * URL → URL. Take only this feature's query keys; names stay the same.
 * Section watches use this so a Results slice ignores sibling params like `scanTab`.
 * Missing / empty values are omitted so `isObjectEmpty` matches the banners list watch.
 * @param {Object} query
 * @param {Object<string, string>|string[]} keys - key map (`Object.values`) or list of query names
 * @example
 * pickQueryParams({ trackerType: 'cookie', scanTab: 'history' }, TRACKERS_QUERY_KEYS)
 * // → { trackerType: 'cookie' }
 */
export function pickQueryParams(query, keys) {
  const keyList = Array.isArray(keys) ? keys : Object.values(keys)
  const picked = {}
  for (const key of keyList) {
    const value = query?.[key]
    if (value == null || value === '') continue
    picked[key] = value
  }
  return picked
}

/**
 * Store → URL. Rename in-memory fields onto namespaced query keys
 * (`{ type, page }` → `{ trackerType, trackerPage }`) so sibling tables can share a route.
 * Missing / empty values are omitted so `sanitizeQuery` / `isObjectEmpty` stay consistent with `pickQueryParams`.
 * @param {Object} state
 * @param {Object<string, string>} keyMap - `{ inMemoryKey: urlKey }` (e.g. TRACKERS_QUERY_KEYS)
 * @example
 * mapToQueryParams({ type: 'cookie', page: 2 }, TRACKERS_QUERY_KEYS)
 * // → { trackerType: 'cookie', trackerPage: 2 }
 */
export function mapToQueryParams(state, keyMap) {
  const mapped = {}
  for (const [stateKey, urlKey] of Object.entries(keyMap)) {
    const value = state?.[stateKey]
    if (value == null || value === '') continue
    mapped[urlKey] = value
  }
  return mapped
}

/**
 * URL → store. Inverse of `mapToQueryParams` — namespaced query keys back onto store field names.
 * Missing / empty values are omitted so downstream `getFilterFromQuery` / `getSortAndPaginationFromQuery` stay consistent with `pickQueryParams`.
 * @param {Object} query
 * @param {Object<string, string>} keyMap - `{ inMemoryKey: urlKey }` (e.g. TRACKERS_QUERY_KEYS)
 * @example
 * mapFromQueryParams({ trackerType: 'cookie', trackerPage: 2 }, TRACKERS_QUERY_KEYS)
 * // → { type: 'cookie', page: 2 }
 */
export function mapFromQueryParams(query, keyMap) {
  const mapped = {}
  for (const [stateKey, urlKey] of Object.entries(keyMap)) {
    const value = query?.[urlKey]
    if (value == null || value === '') continue
    mapped[stateKey] = value
  }
  return mapped
}

/**
 * Wipe this feature's query keys before writing a fresh slice.
 * Never mutate the input (`route.query` is readonly).
 * @param {Object} query
 * @param {Object<string, string>|string[]} keys - key map (`Object.values`) or list of query names
 * @example
 * omitQueryParams({ trackerType: 'cookie', scanTab: 'history' }, TRACKERS_QUERY_KEYS)
 * // → { scanTab: 'history' }
 */
export function omitQueryParams(query, keys) {
  const keyList = Array.isArray(keys) ? keys : Object.values(keys)
  const next = { ...query }
  for (const key of keyList) {
    delete next[key]
  }
  return next
}

/**
 * Case-insensitive email equality; false when either value is missing.
 * @param {string|null|undefined} a
 * @param {string|null|undefined} b
 * @returns {boolean}
 */
export function isSameEmail(a, b) {
  return a && b && a.toLowerCase() === b.toLowerCase()
}

/**
 * At least one letter or number — rejects blank and punctuation-only input.
 * Whitespace-insensitive, so callers may pass raw or trimmed values.
 * @param {string|null|undefined} value
 * @returns {boolean}
 */
export function hasLetterOrNumber(value) {
  return /[\p{L}\p{N}]/u.test(value ?? '')
}

/**
 * @param {string|{ message?: string, code?: string }|null|undefined} errorField
 * @param {string} [fallback='Server error']
 * @returns {string}
 */
export function formatApiErrorDetail(errorField, fallback = 'Server error') {
  if (!errorField) return fallback
  if (typeof errorField === 'string') return errorField
  if (typeof errorField === 'object') {
    return errorField.message || errorField.code || fallback
  }
  return fallback
}

/**
 * Utility classes for a positive/negative boolean state badge (e.g. active/inactive, verified/unverified).
 * @param {boolean} isPositive
 * @returns {string}
 */
export function getToneClass(isPositive) {
  return isPositive ? 'bg-positive-light text-positive' : 'bg-negative-light text-negative'
}

/**
 * Parse a query-string or boolean value to boolean; returns null when missing or unparseable.
 * @param {string|boolean|null|undefined} value
 * @returns {boolean|null}
 */
export function parseBoolean(value) {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value === 'string') {
    const lowercaseValue = value.toLowerCase()
    return lowercaseValue === 'true' || lowercaseValue === '1' || lowercaseValue === 'yes'
  }

  if (typeof value === 'boolean') {
    return value
  }

  return null
}

// Common filter and sort utilities

/**
 * Creates a filter function from query parameters
 * Uses query-string to sanitize by re-parsing the query params
 * @param {Object} defaultFilterFields - Default filter fields from constants
 * @param {Object} query - Query parameters from router
 * @returns {Object} - Filter object
 */
export function getFilterFromQuery(defaultFilterFields, query) {
  // Sanitize query params by round-tripping through query-string
  const sanitizedQuery = queryString.parse(queryString.stringify(query))
  const filterBy = {}
  Object.entries(defaultFilterFields).forEach(([key, defaultValue]) => {
    const value = sanitizedQuery[key] || defaultValue
    if (value !== undefined) {
      if (value === 'true' || value === 'false') {
        filterBy[key] = parseBoolean(value)
      } else {
        filterBy[key] = value
      }
    }
  })
  return filterBy
}

/**
 * Total 1-based page count for a client-side list.
 * @param {number} totalItems
 * @param {number} rowsPerPage
 * @returns {number}
 */
export function getTotalPages(totalItems, rowsPerPage) {
  if (!rowsPerPage || rowsPerPage <= 0 || totalItems <= 0) return 1
  return Math.ceil(totalItems / rowsPerPage)
}

/**
 * "Showing X-Y of Z items" label for client-side paginated tables.
 * @param {{ page: number, rowsPerPage: number, totalItems: number, itemName: string }} options
 * @returns {string}
 */
export function getPaginationLabel({ page, rowsPerPage, totalItems, itemName }) {
  if (!totalItems) return `Showing 0 ${itemName}`
  const start = (page - 1) * rowsPerPage + 1
  const end = Math.min(page * rowsPerPage, totalItems)
  return `Showing ${start}-${end} of ${totalItems} ${itemName}`
}

/**
 * Normalize a 1-based page against a client-side list length.
 * @param {number} page
 * @param {number} totalItems
 * @param {number} rowsPerPage
 * @returns {number}
 */
function clampTablePage(page, totalItems, rowsPerPage) {
  if (totalItems <= 0 || !rowsPerPage || rowsPerPage <= 0) return 1

  const parsed = Number.parseInt(page, 10)
  const normalizedPage = Number.isFinite(parsed) && parsed > 0 ? parsed : 1

  return Math.min(normalizedPage, getTotalPages(totalItems, rowsPerPage))
}

const ROWS_PER_PAGE_SET = new Set(TABLE_ROWS_PER_PAGE_OPTIONS)

function parseRowsPerPage(value, fallback) {
  const parsed = Number.parseInt(value, 10)
  if (Number.isFinite(parsed) && ROWS_PER_PAGE_SET.has(parsed)) return parsed
  return fallback ?? DEFAULT_TABLE_ROWS_PER_PAGE
}

/**
 * Creates sort and pagination parameters from query.
 * Returns `{ tableState, needsUrlHeal }` — tableState is the clean table state object,
 * `needsUrlHeal` is true when the URL contains invalid/clamped values that should be corrected.
 * @param {Object} defaultSortFields - Default sort fields from constants
 * @param {Object} query - Query parameters from router
 * @param {number} [totalItems] - When provided (including 0), page is clamped to valid range
 * @returns {{ tableState: { sortBy: string, descending: boolean, page: number, rowsPerPage: number }, needsUrlHeal: boolean }}
 */
export function getSortAndPaginationFromQuery(defaultSortFields, query = {}, totalItems) {
  const sanitizedQuery = queryString.parse(queryString.stringify(query))

  const sortBy = { ...defaultSortFields }

  let descending = parseBoolean(sanitizedQuery.descending)
  if (descending === null) {
    descending = sortBy.descending
  }

  const rawPage = parseInt(sanitizedQuery.page) || sortBy.page
  const rowsPerPage = parseRowsPerPage(sanitizedQuery.rowsPerPage, defaultSortFields.rowsPerPage)

  const resolvedPage =
    totalItems !== undefined ? clampTablePage(rawPage, totalItems, rowsPerPage) : rawPage

  const needsUrlHeal =
    resolvedPage !== rawPage ||
    (sanitizedQuery.rowsPerPage != null &&
      sanitizedQuery.rowsPerPage !== '' &&
      String(rowsPerPage) !== String(sanitizedQuery.rowsPerPage))

  return {
    tableState: {
      sortBy: sanitizedQuery.sortBy || sortBy.sortBy,
      descending,
      page: resolvedPage,
      rowsPerPage,
    },
    needsUrlHeal,
  }
}

/**
 * Shallow equality for list table state (`sortBy`, `descending`, `page`, `rowsPerPage`).
 * @param {{ sortBy?: string, descending?: boolean, page?: number, rowsPerPage?: number }|null|undefined} a
 * @param {{ sortBy?: string, descending?: boolean, page?: number, rowsPerPage?: number }|null|undefined} b
 * @returns {boolean}
 */
export function isSameTableState(a, b) {
  return (
    a?.sortBy === b?.sortBy &&
    a?.descending === b?.descending &&
    a?.page === b?.page &&
    a?.rowsPerPage === b?.rowsPerPage
  )
}

function isDateValue(value) {
  if (!value || typeof value !== 'string') return false
  const date = new Date(value)
  return !isNaN(date.getTime())
}

function parseValidDate(dateInput) {
  if (!dateInput) return null
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput)
  if (Number.isNaN(date.getTime())) return null
  return date
}

function toUtcPresentationDate(date) {
  return new Date(date.getTime() + date.getTimezoneOffset() * 60000)
}

/**
 * Format a date input with date-fns; returns null for missing or invalid input.
 * @param {string|Date|number} dateInput
 * @param {string} dateFormat - date-fns format string
 * @param {{ utc?: boolean }} options - when true, format using UTC components
 * @returns {string|null}
 */
export function formatDateByFormat(dateInput, dateFormat = 'MMM d', { utc = false } = {}) {
  const date = parseValidDate(dateInput)
  if (!date) return null
  const presentationDate = utc ? toUtcPresentationDate(date) : date
  return format(presentationDate, dateFormat)
}

/**
 * Relative time from now (e.g. "just now", "2 days ago"); returns null for missing or invalid input.
 * Under 60 seconds returns "just now".
 * @param {string|Date|number} dateInput
 * @returns {string|null}
 */
export function formatRelativeTimeFromNow(dateInput) {
  const date = parseValidDate(dateInput)
  if (!date) return null
  const now = new Date()
  if (Math.abs(now.getTime() - date.getTime()) < 60_000) return 'just now'
  return formatDistanceStrict(date, now, { addSuffix: true })
}

/**
 * Relative + absolute date labels for table date cells (last scan, last login, etc.).
 * Returns null when either relative or absolute formatting fails.
 * @param {string|Date|number} dateInput
 * @param {{ dateFormat?: string, titleFormat?: string|null }} [options]
 * @returns {{ relativeLabel: string, dateLabel: string, titleLabel: string|null }|null}
 */
export function formatRelativeDateDisplay(
  dateInput,
  { dateFormat = 'MMM d, yyyy', titleFormat = 'MMM d, yyyy HH:mm' } = {}
) {
  const relativeLabel = formatRelativeTimeFromNow(dateInput)
  const dateLabel = formatDateByFormat(dateInput, dateFormat)
  if (!relativeLabel || !dateLabel) return null
  const titleLabel = titleFormat == null ? null : formatDateByFormat(dateInput, titleFormat)
  return { relativeLabel, dateLabel, titleLabel }
}

/**
 * Resolve a q-table column `name` to its data `field` key.
 * Falls back to `columnName` when the column has no explicit `field`
 * or when the name isn't found in the array.
 * @param {String} columnName - Column `name` from q-table / tableState.sortBy
 * @param {Array}  columns    - Column definitions array (each entry has `name`, `field`)
 * @returns {String} - The data field key to use for row access
 */
export function getColumnSortField(columnName, columns) {
  const column = columns.find((col) => col.name === columnName)
  return column?.field || columnName
}

/**
 * Generic sort function for data arrays
 * @param {Array} items - Array of items to sort
 * @param {String} sortBy - Field to sort by
 * @param {Boolean} descending - Sort in descending order
 * @param {Object} [options] - Optional configuration
 * @param {Function} [options.getValue] - Value resolver; when provided, used instead of `item[sortBy]`
 * @returns {Array} - Sorted array
 */
export function sortItems(items, sortBy, descending, { getValue } = {}) {
  const sorted = [...items].sort((a, b) => {
    const aVal = getValue ? getValue(a) : (a[sortBy] ?? '')
    const bVal = getValue ? getValue(b) : (b[sortBy] ?? '')

    // Handle date fields
    if (isDateValue(aVal) && isDateValue(bVal)) {
      return new Date(aVal) - new Date(bVal)
    }

    // Handle string comparison (case-insensitive, locale-aware)
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return aVal.localeCompare(bVal, undefined, { sensitivity: 'base' })
    }

    // Handle number comparison
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return aVal - bVal
    }

    // Default comparison for mixed types
    return aVal < bVal ? -1 : aVal > bVal ? 1 : 0
  })

  return descending ? sorted.reverse() : sorted
}

/**
 * Paginate an array of items
 * @param {Array} items - Array of items to paginate
 * @param {Number} page - Current page (1-based)
 * @param {Number} rowsPerPage - Number of items per page
 * @returns {Array} - Paginated array
 */
export function paginateItems(items, page, rowsPerPage) {
  const start = (page - 1) * rowsPerPage
  const end = start + rowsPerPage
  return items.slice(start, end)
}

/**
 * Build a { from, to } date range as ISO date strings (YYYY-MM-DD).
 * @param {number} daysBack - how many days before today the range ends
 * @param {number} periodLength - length of the range in days
 * @returns {{ from: string, to: string }}
 */
export function getDateRange(daysBack, periodLength) {
  const to = subDays(new Date(), daysBack)
  const from = subDays(to, periodLength)
  return {
    from: format(from, 'yyyy-MM-dd'),
    to: format(to, 'yyyy-MM-dd'),
  }
}

/**
 * Earliest inclusive date for a scan-history dateRange filter.
 * @param {'1w'|'1m'|'3m'|null|undefined} dateRange
 * @returns {Date|null}
 */
export function getCutoffDate(dateRange) {
  if (!dateRange) return null
  const now = new Date()
  switch (dateRange) {
    case '1w':
      return subDays(now, 7)
    case '1m':
      return subMonths(now, 1)
    case '3m':
      return subMonths(now, 3)
    default:
      return null
  }
}

/**
 * Sanitize query parameters
 * @param {Object} params - Query parameters to sanitize
 * @param {Object} defaults - Default query parameters
 * @returns {Object} - Sanitized query parameters
 */

export function sanitizeQuery(params, defaults) {
  params = { ...params }
  Object.entries(params).forEach(([key, value]) => {
    if (value === defaults[key]) {
      delete params[key]
    }
  })

  const cleanQueryString = queryString.stringify(params, {
    skipNull: true,
    skipEmptyString: true,
  })
  return queryString.parse(cleanQueryString)
}

/**
 * Read and validate a single query param against a Set of allowed values.
 * Returns defaultValue when the param is missing, matches defaultValue, or is invalid.
 *
 * @param {{ query: import('vue-router').LocationQuery, key: string, validValues: Set<string>, defaultValue: string }} options
 * @returns {string}
 */
export function getValidatedQueryParam({ query, key, validValues, defaultValue }) {
  const value = query[key]
  if (!value || value === defaultValue) return defaultValue
  return validValues.has(value) ? value : defaultValue
}

/**
 * Replace (or remove) a single query parameter via router.replace.
 * Removes the key when `value` is falsy or equals `defaultValue`.
 * @param {{ router: import('vue-router').Router, route: import('vue-router').RouteLocationNormalizedLoaded, key: string, value: string|null|undefined, defaultValue: string }} options
 */
export function replaceRouteQueryParam({ router, route, key, value, defaultValue }) {
  const query = { ...route.query }
  if (!value || value === defaultValue) {
    delete query[key]
  } else {
    query[key] = value
  }
  router.replace({ query })
}

/**
 * Open URL in new tab with protocol validation
 * @param {string} url - URL to open
 * @description Validates URL protocol to prevent javascript:, data:, and other dangerous protocols
 */
export function openUrlInNewTab(url) {
  const validation = validateUrlProtocol(url)

  if (!validation.isValid) {
    log.error('openUrlInNewTab: URL validation failed', {
      url,
      error: validation.error,
    })
    notifyService.error(notifyMsgs.error.unknown)
    return
  }

  // Open the validated URL
  window.open(validation.normalizedUrl, '_blank', 'noopener,noreferrer')
}

/**
 * Best-effort hostname from a user-typed URL or bare domain (path / query stripped).
 * @param {string} url
 * @returns {string}
 */
export function extractDomainFromUrl(url) {
  if (!url || !url.length) {
    return ''
  }

  let domain = url
  domain = domain.replace(/^https?:\/\//, '')
  domain = domain.replace(/^www\./, '')
  domain = domain.split('/')[0]
  domain = domain.split('?')[0]

  if (
    !domain ||
    domain.length < 2 ||
    domain.includes(' ') ||
    (!domain.includes('.') && domain.length < 3) ||
    !/^[a-zA-Z0-9.-]+$/.test(domain)
  ) {
    return ''
  }

  return domain
}

/**
 * Get plural form of a string based on count
 * @param {String} singular - Singular form of the word
 * @param {Number} count - Number to determine pluralization
 * @param {Object} options - Options object
 * @param {String} options.plural - Custom plural form (if not provided, will be auto-generated)
 * @returns {String} - Singular or plural form based on count
 */
export function getPlural(singular, count, options = {}) {
  if (count === 1) {
    return singular
  }

  // Use custom plural form if provided
  if (options.plural) {
    return options.plural
  }

  // Auto-generate plural form based on basic English rules
  const lower = singular.toLowerCase()
  const lastChar = lower[lower.length - 1]
  const lastTwoChars = lower.slice(-2)

  // Words ending in s, x, z, ch, sh: add 'es'
  if (
    lastChar === 's' ||
    lastChar === 'x' ||
    lastChar === 'z' ||
    lastTwoChars === 'ch' ||
    lastTwoChars === 'sh'
  ) {
    return singular + 'es'
  }

  // Words ending in 'y' preceded by consonant: replace 'y' with 'ies'
  if (lastChar === 'y' && lower.length > 1) {
    const secondLastChar = lower[lower.length - 2]
    const vowels = ['a', 'e', 'i', 'o', 'u']
    if (!vowels.includes(secondLastChar)) {
      return singular.slice(0, -1) + 'ies'
    }
  }

  // Words ending in 'f': replace 'f' with 'ves'
  if (lastChar === 'f') {
    return singular.slice(0, -1) + 'ves'
  }

  // Words ending in 'fe': replace 'fe' with 'ves'
  if (lastTwoChars === 'fe') {
    return singular.slice(0, -2) + 'ves'
  }

  // Default: add 's'
  return singular + 's'
}

// Map to store color assignments for consistent color per user/admin ID
const colorAssignments = new Map()

export function getConsistentAvatarColor(userId, colorPalette) {
  if (!userId) {
    // Return a random color from the palette when username is falsy
    const randomIndex = Math.floor(Math.random() * colorPalette.length)
    return colorPalette[randomIndex]
  }

  // Check if color was already assigned to this user
  if (colorAssignments.has(userId)) {
    return colorAssignments.get(userId)
  }

  // Calculate color based on ID
  let hash = 0
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32-bit integer
  }

  // Use absolute value and modulo to get index
  const index = Math.abs(hash) % colorPalette.length
  const color = colorPalette[index]

  // Store the assignment for future reference
  colorAssignments.set(userId, color)

  return color
}

export function getNameInitials(name) {
  if (!name) return 'U'
  const names = name
    .trim()
    .split(' ')
    .filter((n) => n.length > 0)
  if (names.length === 0) return 'U'
  if (names.length === 1) return names[0][0].toUpperCase()

  // Take first letter of first name and first letter of last name
  return (names[0][0] + names[names.length - 1][0]).toUpperCase()
}

/**
 * Wraps a mutation's mutateAsync function to catch errors and resolve with undefined
 * instead of rejecting. This prevents Vue warnings about unhandled promise rejections
 * when errors are already handled by the mutation's onError handler.
 *
 * @param {Function} mutateAsync - The mutateAsync function from a Vue Query mutation
 * @returns {Function} - Wrapped function that resolves on error instead of rejecting
 */
export function wrapMutationAsync(mutateAsync) {
  return async (...args) => {
    try {
      return await mutateAsync(...args)
      // eslint-disable-next-line no-unused-vars
    } catch (error) {
      // Error already handled by onError, resolve instead of rejecting
      // to prevent Vue warnings about unhandled promise rejections
      return undefined
    }
  }
}

/**
 * Compare two objects for deep equality using sorted JSON stringification
 * @param {Object} obj1 - First object to compare
 * @param {Object} obj2 - Second object to compare
 * @returns {Boolean} - True if objects are deeply equal
 */
export function areObjectsEqual(obj1, obj2) {
  if (obj1 === obj2) return true
  if (obj1 == null || obj2 == null) return false
  if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false
  if (Array.isArray(obj1) || Array.isArray(obj2)) return false

  const deepSort = (obj) => {
    if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
      return obj
    }
    return Object.keys(obj)
      .sort()
      .reduce((result, key) => {
        result[key] = deepSort(obj[key])
        return result
      }, {})
  }

  return JSON.stringify(deepSort(obj1)) === JSON.stringify(deepSort(obj2))
}

/**
 * Walk DOM ancestors to find the first scrollable parent element.
 * @param {Element} el - Starting element
 * @returns {Element|Window} The scrollable parent or window
 */
export function findScrollableParent(el) {
  while (el && el !== document.body) {
    const style = getComputedStyle(el)
    if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
      return el
    }
    el = el.parentElement
  }
  return window
}
